import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useMemo, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import { usePhotoUrl, withoutUnpublishedOwnPhotos, type PostMinimal } from '../posts';
import { IdeaWorkspaceMiniMap, type IdeaWorkspaceMiniMapHandle } from './IdeaWorkspaceMiniMap';
import { ReferenceGrid } from './ReferenceGrid';
import { ReportPdfTemplate } from './ReportPdfTemplate';
import { generateIdeaPdf } from './generateIdeaPdf';
import { getInsightColor } from './insightMapHelpers';
import { splitActionIdea } from './ideaFormHelpers';
import type { IdeaMinimal } from './useEcwIdeas';
import type { InsightMinimal } from './useEcwInsights';

/**
 * 処方箋カード詳細 modal (docs/107 Wave8b、old2 `IdeaCardList.tsx` の `IdeaCardDetailView`
 * (L162-870) を v2 idiomatic に移植)。
 *
 * Wave8b で old2 parity に拡張した点:
 *   - **3 カラム詳細レイアウト** (old2 L511-854): 左=ベースとした気づき一覧/詳細、
 *     中央=キャッチ画像+気づき別色番号付きミニマップ+処方/効能+AI 対話記録、
 *     右=投稿詳細 (v2 では onSelectPost で親 PostDetailModal へ移譲)。
 *   - **キャッチ画像ピッカー** (old2 L723-746): アップロード / 投稿写真から選択 / 現在の地図を
 *     キャプチャ。`referenceImageBase64` が初期値、無ければ先頭投稿写真。
 *   - **気づき別カラー番号付きミニマップ** (old2 L271-319): `IdeaWorkspaceMiniMap` を back モードで
 *     再利用 (insight 別 8 色 + 投稿の全体 index 番号)。
 *   - **気づきインライン詳細** (old2 L524-607): 左カラムで気づきを選ぶと AI 対話 STEP 全文を
 *     インライン展開 (STEP0..STEP3、症状/診断)。
 *   - **actionIdea 【効能】分割** (old2 L791/L809): 「処方・アクション」と「効能」を別表示。
 *
 * v2 good-diff として維持: PDF 出力 (隠し DOM テンプレ)、AI 提案 references grid、
 * オフライン写真 fallback (usePhotoUrl + doc URL)。
 */

const INSIGHT_COLOR_FALLBACK = '#888';

export interface IdeaDetailModalProps {
  open: boolean;
  onClose: () => void;
  idea: IdeaMinimal | null;
  posts: PostMinimal[];
  insights: InsightMinimal[];
  /** 投稿クリック時に親 (AnalysisPage) の PostDetailModal を開く (old2 の右カラム DetailPanel 代替)。 */
  onSelectPost?: (post: PostMinimal) => void;
  /** districtId — ミニマップの pmtiles 解決に使用 (未指定なら OSM fallback)。 */
  districtId?: string;
  /** sessionId から解決したセッション名 (内部 ID は出さない)。未解決/無紐付けは null。 */
  sessionName?: string | null;
}

export function IdeaDetailModal({
  open,
  onClose,
  idea,
  posts,
  insights,
  onSelectPost,
  districtId = '',
  sessionName,
}: IdeaDetailModalProps) {
  const { toast } = useToast();

  // docs/28 レビュー A5: アイデアのカード・PDF 帳票には、本人だけが見られるぼかす前の写真を載せない。
  const linkedPosts = useMemo(
    () =>
      idea
        ? withoutUnpublishedOwnPhotos(posts.filter((p) => idea.selectedPostIds.includes(p.id)))
        : [],
    [idea, posts],
  );
  // old2 L193-196: insightIds で紐づく気づきを抽出 (順序は idea.insightIds 順 = 色割当の基準)。
  const linkedInsights = useMemo(() => {
    if (!idea || idea.insightIds.length === 0) return [];
    const byId = new Map(insights.map((i) => [i.id, i]));
    return idea.insightIds
      .map((id) => byId.get(id))
      .filter((i): i is InsightMinimal => i !== undefined);
  }, [idea, insights]);
  // old2 L199-205: 気づき index → 色 (8 色循環パレット)。
  const insightColorMap = useMemo(() => {
    const map = new Map<string, string>();
    linkedInsights.forEach((ins, idx) => map.set(ins.id, getInsightColor(idx)));
    return map;
  }, [linkedInsights]);

  const postsWithPhoto = useMemo(
    () => linkedPosts.filter((p) => p.photoThumbPath !== null),
    [linkedPosts],
  );

  // docs/107 Wave8b / old2 L791,L809: actionIdea を 処方 / 効能 に分割。
  const { prescription, effect } = useMemo(
    () => splitActionIdea(idea?.actionIdea),
    [idea?.actionIdea],
  );

  // ---- キャッチ画像 (old2 L245-257, L321-341) ----
  // 表示優先順位: ユーザー手動指定 (manualCatchUrl) → referenceImageBase64 → 先頭投稿写真 → なし。
  // 手動指定はアップロード / 投稿写真選択 / 地図キャプチャで上書き。idea 切替時はリセット。
  const ideaId = idea?.id ?? null;
  const [manualCatchUrl, setManualCatchUrl] = useState<string>('');
  const [showCatchPicker, setShowCatchPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const miniMapRef = useRef<IdeaWorkspaceMiniMapHandle | null>(null);
  // ---- 左カラム: 選択中の気づき (inline 詳細) ----
  const [selectedInsight, setSelectedInsight] = useState<InsightMinimal | null>(null);

  // idea が切り替わったら手動上書き / picker / inline 詳細をリセット (React 推奨の
  // 「render 中に prev を比較して setState」パターン、effect 不要)。
  const [prevIdeaId, setPrevIdeaId] = useState<string | null>(ideaId);
  if (ideaId !== prevIdeaId) {
    setPrevIdeaId(ideaId);
    setManualCatchUrl('');
    setShowCatchPicker(false);
    setSelectedInsight(null);
  }

  // 先頭投稿写真を catch 画像の fallback として resolve (referenceImageBase64 が無いときのみ)。
  const firstPhotoPath = !idea?.referenceImageBase64
    ? (linkedPosts[0]?.photoThumbPath ?? null)
    : null;
  const firstPhoto = usePhotoUrl(firstPhotoPath, linkedPosts[0]?.photoThumbUrl ?? null);

  // 表示する catch 画像 URL を派生 (state でなく derived value → effect 同期不要)。
  const catchImageUrl =
    manualCatchUrl ||
    idea?.referenceImageBase64 ||
    (firstPhoto.status === 'ready' ? (firstPhoto.url ?? '') : '');

  // ---- PDF (v2 good-diff、Phase B+2-1) ----
  const pdfTemplateRef = useRef<HTMLDivElement | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setManualCatchUrl((ev.target?.result as string) ?? '');
      setShowCatchPicker(false);
    };
    reader.readAsDataURL(file);
  };

  const handleCaptureMap = async () => {
    try {
      const dataUrl = await miniMapRef.current?.capture();
      if (dataUrl) {
        setManualCatchUrl(dataUrl);
        setShowCatchPicker(false);
      } else {
        toast('warning', '地図のキャプチャに失敗しました');
      }
    } catch {
      toast('warning', '地図のキャプチャに失敗しました');
    }
  };

  const handleGeneratePdf = async () => {
    if (!idea || !pdfTemplateRef.current) return;
    setPdfLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      await generateIdeaPdf({
        templateRef: pdfTemplateRef.current,
        fileName: `Prescription_${idea.title || idea.id}.pdf`,
        mapElement: mapWrapperRef.current ?? null,
        resolvePhotoUrl: async (path) => {
          try {
            return await getDownloadURL(storageRef(storage, path));
          } catch {
            return null;
          }
        },
      });
      toast('success', 'PDF を出力しました');
    } catch (e) {
      console.error('[IdeaDetailModal] PDF generation failed', e);
      toast('error', `PDF 出力に失敗しました: ${(e as Error).message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  if (!idea) return null;

  return (
    <Modal open={open} onClose={onClose} title="処方箋カード詳細" size="xl">
      <div className="flex flex-col lg:flex-row gap-3" data-testid="idea-detail-3col">
        {/* ============ 左カラム: ベースとした気づき一覧 / inline 詳細 ============ */}
        <aside className="lg:w-[280px] lg:shrink-0 lg:border-r lg:border-slate-200 lg:pr-3">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">💡 ベースとした気づき一覧</h3>
          {selectedInsight ? (
            <InsightInlineDetail
              insight={selectedInsight}
              posts={posts}
              color={insightColorMap.get(selectedInsight.id) ?? INSIGHT_COLOR_FALLBACK}
              onBack={() => setSelectedInsight(null)}
              onSelectPost={onSelectPost}
            />
          ) : (
            <ul className="flex flex-col gap-2" data-testid="idea-detail-insight-list">
              {linkedInsights.map((ins, idx) => (
                <InsightSummaryCard
                  key={ins.id}
                  insight={ins}
                  index={idx}
                  color={insightColorMap.get(ins.id) ?? INSIGHT_COLOR_FALLBACK}
                  posts={posts}
                  onClick={() => setSelectedInsight(ins)}
                  onSelectPost={onSelectPost}
                />
              ))}
              {linkedInsights.length === 0 && (
                <li className="text-xs text-slate-400">設定された気づきがありません</li>
              )}
            </ul>
          )}
        </aside>

        {/* ============ 中央カラム: 処方箋内容 ============ */}
        <div className="flex-1 min-w-0">
          {/* タイトル + メタ + 感情/分類 chip (old2 L695-706) */}
          <div className="mb-3">
            {(idea.emotionTag || idea.categoryLabel) && (
              <div className="flex flex-wrap gap-1 mb-1">
                {idea.emotionTag && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                    {idea.emotionTag}
                  </span>
                )}
                {idea.categoryLabel && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                    {idea.categoryLabel}
                  </span>
                )}
              </div>
            )}
            <h3 className="font-bold text-lg text-slate-900 break-words leading-snug">
              {idea.title}
            </h3>
            <p className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
              <span>作成者: {idea.nickname || '匿名'}</span>
              {sessionName && <span>セッション: {sessionName}</span>}
              {idea.team && <span>チーム: {idea.team}</span>}
            </p>
            {idea.emotionDescription && (
              <p className="text-[11px] text-slate-500 mt-1 italic">{idea.emotionDescription}</p>
            )}
          </div>

          {/* キャッチ画像 + 地図 を横並び (old2 L708-756) */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            {/* キャッチ画像 + ピッカー */}
            <div className="flex-1 relative min-w-0">
              {catchImageUrl ? (
                <img
                  src={catchImageUrl}
                  alt={`${idea.title} のキャッチ画像`}
                  loading="lazy"
                  className="w-full h-[220px] object-contain rounded-lg bg-white border border-slate-200"
                />
              ) : (
                <div className="w-full h-[220px] rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-3xl text-slate-400">
                  📷
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowCatchPicker((v) => !v)}
                className="absolute bottom-2 right-2 text-[11px] px-2 py-1 rounded bg-slate-900/70 text-white hover:bg-slate-900/85 transition-colors"
                aria-label="キャッチ画像を変更"
              >
                🖼 画像を変更
              </button>

              {showCatchPicker && (
                <div
                  className="absolute z-10 bottom-full left-0 right-0 mb-2 p-3 bg-white rounded-lg border border-slate-200 shadow-lg text-xs"
                  data-testid="catch-image-picker"
                >
                  <div className="font-semibold text-slate-700 mb-2">キャッチ画像を選択</div>
                  <div className="mb-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      📤 画像をアップロード
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                      aria-label="キャッチ画像をアップロード"
                    />
                  </div>
                  {postsWithPhoto.length > 0 && (
                    <div className="mb-2">
                      <div className="font-medium text-slate-500 mb-1">投稿写真から選択:</div>
                      <ul className="flex flex-wrap gap-1.5">
                        {postsWithPhoto.map((p) => (
                          <li key={p.id}>
                            <CatchPickerThumb
                              post={p}
                              onPick={(url) => {
                                setManualCatchUrl(url);
                                setShowCatchPicker(false);
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button variant="primary" size="sm" onClick={handleCaptureMap}>
                    🗺 現在の地図をキャプチャして使用
                  </Button>
                </div>
              )}
            </div>

            {/* 気づき別カラー番号付きミニマップ (old2 L749-755) */}
            <div className="flex-1 relative min-w-0" ref={mapWrapperRef}>
              <div className="absolute top-2 left-2 z-[1] bg-white/90 px-2 py-0.5 rounded-full text-[11px] font-semibold text-slate-700 shadow-sm pointer-events-none">
                📍 紐づけられた市民データ
              </div>
              <IdeaWorkspaceMiniMap
                ref={miniMapRef}
                districtId={districtId}
                mode="back"
                selectedPosts={linkedPosts}
                selectedInsights={linkedInsights}
                selectedInsightId={selectedInsight?.id ?? null}
                onSelectPost={onSelectPost}
                onSelectInsight={(ins) => setSelectedInsight(ins)}
                className="w-full h-[220px] rounded-lg border border-slate-200 overflow-hidden bg-slate-100"
              />
            </div>
          </div>

          {/* 処方・アクション (old2 L758-762) */}
          <section className="mb-3">
            <h4 className="text-xs font-semibold text-slate-700 mb-1">📝 処方・アクション</h4>
            <p className="text-sm text-slate-800 whitespace-pre-wrap bg-amber-50 rounded p-2 border border-amber-200">
              {prescription || '（未記入）'}
            </p>
          </section>

          {/* 効能 (old2 L805-811 の【効能】) */}
          {effect && (
            <section className="mb-3">
              <h4 className="text-xs font-semibold text-emerald-700 mb-1">✨ 効能</h4>
              <p className="text-sm text-slate-800 whitespace-pre-wrap bg-emerald-50 rounded p-2 border border-emerald-200">
                {effect}
              </p>
            </section>
          )}

          {/* AI 対話記録 (old2 L764-819) */}
          {(idea.llmInsightSummary ||
            (idea.llmActionQuestions && idea.llmActionQuestions.length > 0) ||
            (idea.llmEffectQuestions && idea.llmEffectQuestions.length > 0)) && (
            <section className="mb-3">
              <h4 className="text-xs font-semibold text-primary-700 mb-2">
                🤖 処方箋検討時の AI 対話記録
              </h4>
              <div className="space-y-2 text-xs">
                {/* 1. 選択した気づきの概要 (old2 L771-775) */}
                <div className="rounded p-2 bg-amber-50 border border-amber-200">
                  <div className="font-semibold text-amber-800 mb-1">💡 選択した気づきの概要</div>
                  <p className="whitespace-pre-wrap text-slate-800">
                    {idea.llmInsightSummary || idea.observations || '（記録なし）'}
                  </p>
                </div>
                {/* 2. STEP4 AI 問いかけ (old2 L778-785) */}
                {idea.llmActionQuestions && idea.llmActionQuestions.length > 0 && (
                  <div className="rounded p-2 bg-slate-50 border border-slate-200">
                    <div className="font-semibold text-primary-700 mb-1">
                      STEP 4: AI からの問いかけ（処方について）
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                      {idea.llmActionQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 3. STEP4 参加者の回答 = 処方 (old2 L788-793) */}
                <div className="rounded p-2 bg-amber-50 border border-amber-200">
                  <div className="font-semibold text-amber-800 mb-1">
                    ✍️ 参加者の回答（処方・アクション）
                  </div>
                  <p className="whitespace-pre-wrap text-slate-800">
                    {prescription || '（未記入）'}
                  </p>
                </div>
                {/* 4. STEP5 AI 問いかけ (old2 L796-803) */}
                {idea.llmEffectQuestions && idea.llmEffectQuestions.length > 0 && (
                  <div className="rounded p-2 bg-slate-50 border border-slate-200">
                    <div className="font-semibold text-primary-700 mb-1">
                      STEP 5: AI からの問いかけ（効能について）
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                      {idea.llmEffectQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 5. STEP5 参加者の回答 = 効能 (old2 L806-811) */}
                <div className="rounded p-2 bg-amber-50 border border-amber-200">
                  <div className="font-semibold text-amber-800 mb-1">✍️ 参加者の回答（効能）</div>
                  <p className="whitespace-pre-wrap text-slate-800">{effect || '（未記入）'}</p>
                </div>
              </div>
            </section>
          )}

          {/* AI 提案 references grid (v2 good-diff、Sprint AW) */}
          {idea.references && idea.references.length > 0 && (
            <section className="mb-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                💡 AI 提案の参考事例 ({idea.references.length} 件)
              </h4>
              <ReferenceGrid references={idea.references} variant="detail" />
            </section>
          )}

          {linkedPosts.length === 0 && linkedInsights.length === 0 && (
            <p className="text-xs text-slate-500 italic">紐づく投稿・気づきはありません</p>
          )}

          {/* PDF 出力 footer (v2 good-diff) */}
          <div className="flex justify-end pt-3 border-t border-slate-200 mt-3">
            <Button
              variant="primary"
              size="md"
              onClick={handleGeneratePdf}
              disabled={pdfLoading}
              aria-label="処方箋カードを PDF として出力"
            >
              {pdfLoading ? '📄 PDF 生成中…' : '📄 PDF を出力'}
            </Button>
          </div>
        </div>
      </div>

      {/* 隠し DOM (html2canvas の対象、画面には見せない)。 */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '794px',
          opacity: 0,
          zIndex: -9999,
          pointerEvents: 'none',
        }}
      >
        <ReportPdfTemplate
          ref={pdfTemplateRef}
          idea={idea}
          posts={linkedPosts}
          insights={linkedInsights}
          catchImageUrl={catchImageUrl}
          mapImageUrl=""
        />
      </div>
    </Modal>
  );
}

/* ============================================================================
 * 左カラム — 気づきサマリカード (old2 L611-683)
 * ========================================================================== */

function InsightSummaryCard({
  insight,
  index,
  color,
  posts,
  onClick,
  onSelectPost,
}: {
  insight: InsightMinimal;
  index: number;
  color: string;
  posts: PostMinimal[];
  onClick: () => void;
  onSelectPost?: (post: PostMinimal) => void;
}) {
  const insPosts = useMemo(
    () => posts.filter((p) => insight.selectedPostIds.includes(p.id)),
    [posts, insight.selectedPostIds],
  );
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left p-2.5 rounded-md border-l-4 bg-slate-50 hover:bg-white shadow-sm transition-colors flex flex-col gap-1.5"
        style={{ borderLeftColor: color }}
        data-testid="idea-detail-insight-summary"
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-500">気づき {index + 1}</span>
          <span className="flex gap-1">
            {insight.emotionTag && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-rose-100 text-rose-700">
                {insight.emotionTag}
              </span>
            )}
            {insight.categoryLabel && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-slate-200 text-slate-700">
                {insight.categoryLabel}
              </span>
            )}
          </span>
        </div>
        {insight.title && (
          <div className="text-[13px] font-bold text-slate-800 leading-snug">
            💡 {insight.title}
          </div>
        )}
        {insPosts.length > 0 && (
          <div className="flex gap-1 overflow-x-auto pb-0.5">
            {insPosts.map((p) => (
              <span
                key={p.id}
                role={onSelectPost ? 'button' : undefined}
                tabIndex={onSelectPost ? 0 : undefined}
                onClick={(e) => {
                  if (!onSelectPost) return;
                  e.stopPropagation();
                  onSelectPost(p);
                }}
                onKeyDown={(e) => {
                  if (onSelectPost && (e.key === 'Enter' || e.key === ' ')) {
                    e.stopPropagation();
                    e.preventDefault();
                    onSelectPost(p);
                  }
                }}
                className="shrink-0"
              >
                <SmallPostThumb post={p} size={36} />
              </span>
            ))}
          </div>
        )}
        {insight.observations && (
          <div className="text-[11px] font-medium text-slate-600">
            症状: {trim(insight.observations, 40)}
          </div>
        )}
        {insight.diagnosis && (
          <div className="text-[10px] text-slate-500">診断: {trim(insight.diagnosis, 40)}</div>
        )}
        <div className="flex justify-end">
          <span className="text-[11px] font-semibold text-primary-700">詳細を見る →</span>
        </div>
      </button>
    </li>
  );
}

/* ============================================================================
 * 左カラム — 気づきインライン詳細 (AI 対話 STEP 全文、old2 L524-607)
 * ========================================================================== */

function InsightInlineDetail({
  insight,
  posts,
  color,
  onBack,
  onSelectPost,
}: {
  insight: InsightMinimal;
  posts: PostMinimal[];
  color: string;
  onBack: () => void;
  onSelectPost?: (post: PostMinimal) => void;
}) {
  const insPosts = useMemo(
    () => posts.filter((p) => insight.selectedPostIds.includes(p.id)),
    [posts, insight.selectedPostIds],
  );
  return (
    <div data-testid="idea-detail-insight-inline">
      <button
        type="button"
        onClick={onBack}
        className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 mb-3"
      >
        ← 気づき一覧に戻る
      </button>
      <div className="pl-3 border-l-4" style={{ borderLeftColor: color }}>
        {insight.title && (
          <h4 className="text-base font-extrabold text-slate-800 mb-3">💡 {insight.title}</h4>
        )}

        {/* ベースとなった市民データ (old2 L539-557) */}
        {insPosts.length > 0 && (
          <div className="mb-4">
            <h5 className="text-xs font-semibold text-primary-700 mb-1.5">
              📋 ベースとなった市民データ
            </h5>
            <div className="flex flex-wrap gap-1.5">
              {insPosts.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelectPost?.(p)}
                  className="w-[88px] rounded border border-slate-200 overflow-hidden bg-white text-left hover:border-primary-400 transition-colors"
                  title={p.title || '(無題)'}
                >
                  <SmallPostThumb post={p} size={56} full />
                  <div className="px-1 py-0.5 text-[9px] font-medium truncate flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-primary-500 text-white text-[8px]">
                      {idx + 1}
                    </span>
                    {p.title || '(無題)'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI 対話ログ (old2 L559-600 = STEP0..STEP3 + 症状/診断) */}
        <div className="mb-3">
          <h5 className="text-xs font-semibold text-primary-700 mb-1.5">🤖 AI との対話</h5>
          <div className="space-y-2 text-xs">
            {insight.initialThoughts && insight.initialThoughts.trim().length > 0 && (
              <div className="rounded p-2 bg-amber-50 border border-amber-200">
                <div className="font-semibold text-amber-800 mb-1">
                  ✍️ STEP 0: 参加者の最初の感想
                </div>
                <p className="whitespace-pre-wrap text-slate-800">{insight.initialThoughts}</p>
              </div>
            )}
            <div className="rounded p-2 bg-slate-50 border border-slate-200">
              <div className="font-semibold text-primary-700 mb-1">STEP 1: AI からの所見</div>
              <p className="whitespace-pre-wrap text-slate-800">
                {insight.llmAnalysis || '（記録なし）'}
              </p>
            </div>
            {insight.llmResolutionQuestions && insight.llmResolutionQuestions.length > 0 && (
              <div className="rounded p-2 bg-slate-50 border border-slate-200">
                <div className="font-semibold text-primary-700 mb-1">STEP 2: AI からの問いかけ</div>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                  {insight.llmResolutionQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="rounded p-2 bg-amber-50 border border-amber-200">
              <div className="font-semibold text-amber-800 mb-1">✍️ 参加者の回答（症状）</div>
              <p className="whitespace-pre-wrap text-slate-800">
                {insight.observations || '（未記入）'}
              </p>
            </div>
            {insight.llmDiagnosisQuestions && insight.llmDiagnosisQuestions.length > 0 && (
              <div className="rounded p-2 bg-slate-50 border border-slate-200">
                <div className="font-semibold text-primary-700 mb-1">STEP 3: AI からの問いかけ</div>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                  {insight.llmDiagnosisQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="rounded p-2 bg-amber-50 border border-amber-200">
              <div className="font-semibold text-amber-800 mb-1">✍️ 参加者の回答（診断）</div>
              <p className="whitespace-pre-wrap text-slate-800">
                {insight.diagnosis || '（未記入）'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-[11px] text-slate-500">
          <div>投稿者: {insight.nickname || '匿名'}</div>
          {insight.team && <div>チーム: {insight.team}</div>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 小さな写真サムネ (catch picker / 気づきカード用)
 * ========================================================================== */

function SmallPostThumb({
  post,
  size,
  full,
}: {
  post: PostMinimal;
  size: number;
  /** full=true なら width 100% (市民データカード上部用)。 */
  full?: boolean;
}) {
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl);
  const style = full
    ? ({ width: '100%', height: `${size}px` } as React.CSSProperties)
    : ({ width: `${size}px`, height: `${size}px` } as React.CSSProperties);
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className="object-cover rounded border border-slate-200 bg-slate-100"
        style={style}
      />
    );
  }
  return (
    <span
      aria-label={photo.status === 'failed' ? '写真の読込に失敗' : '写真読込中'}
      className={`flex items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-400 text-xs ${photo.status === 'loading' ? 'animate-pulse' : ''}`}
      style={style}
    >
      📷
    </span>
  );
}

/** キャッチ画像ピッカーの投稿写真サムネ (クリックで catch 画像に採用、解決済 URL を渡す)。 */
function CatchPickerThumb({ post, onPick }: { post: PostMinimal; onPick: (url: string) => void }) {
  // 大きめ (photoPath / photoUrl 原寸) を優先し、無ければ thumb。
  const photo = usePhotoUrl(
    post.photoPath ?? post.photoThumbPath,
    post.photoUrl ?? post.photoThumbUrl,
  );
  const ready = photo.status === 'ready' && photo.url;
  return (
    <button
      type="button"
      onClick={() => ready && photo.url && onPick(photo.url)}
      disabled={!ready}
      className="w-[60px] h-[45px] rounded border-2 border-transparent hover:border-primary-400 overflow-hidden bg-slate-100 disabled:opacity-50"
      aria-label={`「${post.title || '無題'}」の写真をキャッチ画像に使用`}
      title={post.title || '(無題)'}
    >
      {ready ? (
        <img src={photo.url} alt="" className="w-full h-full object-contain" loading="lazy" />
      ) : (
        <span className="flex items-center justify-center w-full h-full text-slate-400 text-xs">
          📷
        </span>
      )}
    </button>
  );
}

/** 指定文字数でトリム (old2 L670/L673 の substring(0,40) + '...' 踏襲)。 */
function trim(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}...` : s;
}
